import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  AmountCell,
  FinancialStatementStyles,
  NodeNameCell,
  PresetChips,
  amount,
  nodeKey,
  periodPresets,
  printStatement,
} from "./FinancialStatementShared";

const TITLE = "Receipts and Payments Voucher Summary";

// A parent account as an expandable node whose children are the heads of
// account posted under it. `prefix` keeps receipt and payment groups apart.
const groupNode = (prefix, group) => ({
  key: `${prefix}-${group.id ?? "top"}`,
  code: group.code,
  name: group.name,
  status: group.status,
  amount: group.amount,
  children: group.accounts.map((account) => ({ ...account, children: [] })),
});

const buildRows = (data, collapsed) => {
  const rows = [];

  const sideRows = (prefix, block) => {
    block.groups
      .map((group) => groupNode(prefix, group))
      .forEach((node) => {
        const open = !collapsed[nodeKey(node)];
        rows.push({
          type: "group",
          key: node.key,
          depth: 1,
          node,
          hasChildren: node.children.length > 0,
          expanded: open,
          amount: node.amount,
        });
        if (open) {
          node.children.forEach((account) =>
            rows.push({
              type: "account",
              key: `${node.key}-${account.id}`,
              depth: 2,
              node: account,
              hasChildren: false,
              amount: account.amount,
            }),
          );
        }
      });
  };

  rows.push({ type: "section", key: "sec-receipts", label: "Receipts From" });
  if (!data.journal_code) {
    data.opening_codes.forEach((code) =>
      rows.push({
        type: "line",
        key: `opening-${code.id}`,
        depth: 1,
        node: { code: code.code, name: code.name, children: [] },
        hasChildren: false,
        amount: code.amount,
      }),
    );
  }
  rows.push({
    type: "subtotal",
    key: "opening",
    label: "Opening Balance",
    amount: data.opening,
  });
  sideRows("r", data.receipts);
  rows.push({
    type: "subtotal",
    key: "total-receipts",
    label: "Total Receipts",
    amount: data.receipts.total,
  });
  rows.push({
    type: "total",
    key: "total-available",
    label: "Total Cash Available",
    amount: data.total_available,
  });

  rows.push({ type: "section", key: "sec-payments", label: "Payments To" });
  sideRows("p", data.payments);
  rows.push({
    type: "subtotal",
    key: "total-payments",
    label: "Total Payments",
    amount: data.payments.total,
  });
  rows.push({
    type: "grand",
    key: "closing",
    label: "Closing Balance",
    amount: data.closing,
  });

  return rows;
};

const ReceiptsPaymentsVoucherSummary = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [, thisMonthFrom, thisMonthTo] = periodPresets()[0];
  const [inputs, setInputs] = useState({
    from: searchParams.get("from") || thisMonthFrom,
    to: searchParams.get("to") || thisMonthTo,
    journal_code: searchParams.get("journal_code") || "",
  });
  const [data, setData] = useState(null);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  // Groups start open, as in the printed report; this holds the closed ones.
  const [collapsed, setCollapsed] = useState({});

  const fetchData = async (params = inputs) => {
    setLoading(true);
    setAlert(null);
    const query = { from: params.from, to: params.to };
    if (params.journal_code) query.journal_code = params.journal_code;
    setSearchParams(query, { replace: true });

    try {
      const res = await Http.get("/receipts-payments-voucher-summary", {
        params: query,
      });
      setData(res.data);
      setCodes(res.data.codes);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the report. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const apply = (changes) => {
    const next = { ...inputs, ...changes };
    setInputs(next);
    fetchData(next);
  };

  const rows = useMemo(
    () => (data ? buildRows(data, collapsed) : []),
    [data, collapsed],
  );

  const expandable = useMemo(
    () =>
      data
        ? [
            ...data.receipts.groups.map((g) => nodeKey(groupNode("r", g))),
            ...data.payments.groups.map((g) => nodeKey(groupNode("p", g))),
          ]
        : [],
    [data],
  );

  const allExpanded = expandable.every((key) => !collapsed[key]);

  const toggleAll = () =>
    setCollapsed(
      allExpanded
        ? expandable.reduce((acc, key) => ({ ...acc, [key]: true }), {})
        : {},
    );

  const toggle = (node) => {
    const key = nodeKey(node);
    setCollapsed((values) => ({ ...values, [key]: !values[key] }));
  };

  const periodLabel = data
    ? `${formatDate(data.from)} – ${formatDate(data.to)}`
    : `${formatDate(inputs.from)} – ${formatDate(inputs.to)}`;

  const codeLabel = data?.journal_code
    ? `${data.journal_code.code} - ${data.journal_code.name}`
    : "All journal codes";

  const renderRow = (row) => {
    if (row.type === "section") {
      return (
        <tr key={row.key} className="fs-section">
          <td colSpan={3}>{row.label}</td>
        </tr>
      );
    }
    const isNode = !!row.node;
    return (
      <tr key={row.key} className={`fs-${row.type}`}>
        {isNode ? (
          <NodeNameCell row={row} onToggle={toggle} />
        ) : (
          <td>{row.label}</td>
        )}
        <td className="fs-code">{isNode ? row.node.code : ""}</td>
        <AmountCell value={row.amount} />
      </tr>
    );
  };

  const handlePrint = () => {
    const headHtml = `
      <th>Head of Accounts</th><th>Acc-Code</th><th class="num">Amount</th>`;
    const bodyHtml = rows
      .map((row) => {
        if (row.type === "section") {
          return `<tr class="section"><td colspan="3">${row.label}</td></tr>`;
        }
        const isNode = !!row.node;
        const pad = isNode ? 16 + row.depth * 16 : 8;
        return `
          <tr class="${row.type}">
            <td style="padding-left:${pad}px">${isNode ? row.node.name : row.label}</td>
            <td class="code">${isNode && row.node.code ? row.node.code : ""}</td>
            <td class="num">${amount(row.amount)}</td>
          </tr>`;
      })
      .join("");

    printStatement({
      title: TITLE,
      subtitle: `${codeLabel} &middot; Statement from ${periodLabel} &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
      note: "Receipt and payment vouchers only. Receipts are the credit lines of receipt vouchers, payments the debit lines of payment vouchers; opening balance is receipts less payments before the start date.",
    });
  };

  const stat = (label, icon, value, sub, className = "") => (
    <div className="col-md-3 mg-b-10">
      <div className={`coa-stat ${className}`}>
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{value}</span>
        <span className="coa-stat-sub">{sub}</span>
      </div>
    </div>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">{TITLE}</h5>
            <p className="text-muted mb-0 tx-12">
              {codeLabel} · {periodLabel}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !data}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-filter me-2"></i>Filters
              </span>
              {data && (
                <span className="coa-head-note">
                  {data.vouchers} voucher{data.vouchers === 1 ? "" : "s"} in
                  period
                </span>
              )}
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-3">
                  <label>Journal Code</label>
                  <select
                    className="form-control"
                    name="journal_code"
                    value={inputs.journal_code}
                    onChange={(e) => apply({ journal_code: e.target.value })}
                  >
                    <option value="">All journal codes</option>
                    {codes.map((code) => (
                      <option key={code.id} value={code.id}>
                        {code.code} - {code.name}
                        {code.status === "inactive" ? " (inactive)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-6 mg-t-10 mg-lg-t-0">
                  <label>Period</label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control"
                      name="from"
                      value={inputs.from}
                      onChange={handleChange}
                      max={inputs.to}
                      aria-label="From"
                    />
                    <input
                      type="date"
                      className="form-control"
                      name="to"
                      value={inputs.to}
                      onChange={handleChange}
                      min={inputs.from}
                      aria-label="To"
                    />
                  </div>
                  <PresetChips
                    presets={periodPresets()}
                    isActive={([, from, to]) =>
                      inputs.from === from && inputs.to === to
                    }
                    onPick={([, from, to]) => apply({ from, to })}
                  />
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchData()}
                    disabled={loading || !inputs.from || !inputs.to}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {data && (
            <div className="row mg-b-20">
              {stat(
                "Opening Balance",
                "fa-flag-o",
                amount(data.opening),
                `Before ${formatDate(data.from)}`,
              )}
              {stat(
                "Total Receipts",
                "fa-arrow-circle-down",
                amount(data.receipts.total),
                `Cash available ${amount(data.total_available)}`,
                "is-ok",
              )}
              {stat(
                "Total Payments",
                "fa-arrow-circle-up",
                amount(data.payments.total),
                `${data.payments.groups.length} account group${data.payments.groups.length === 1 ? "" : "s"}`,
                "is-alert",
              )}
              {stat(
                "Closing Balance",
                "fa-university",
                amount(data.closing),
                `As at ${formatDate(data.to)}`,
                num(data.closing) < 0 ? "is-alert" : "",
              )}
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-file-text-o me-2"></i>
                {TITLE}
                <span className="coa-head-note mg-l-10">Amounts in Taka</span>
              </span>
              {data && expandable.length > 0 && (
                <span className="coa-no-print">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={toggleAll}
                  >
                    <i
                      className={`fa fa-${allExpanded ? "compress" : "expand"} mg-r-6`}
                    ></i>
                    {allExpanded ? "Collapse all" : "Expand all"}
                  </button>
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The report could not be loaded.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="fs-table">
                  <thead>
                    <tr>
                      <th>Head of Accounts</th>
                      <th>Acc-Code</th>
                      <th className="coa-num">Amount</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {rows.map(renderRow)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptsPaymentsVoucherSummary;
